import React from 'react';
import { connect } from 'react-redux';
import { Grid, Box, Typography, Chip, Switch, Select, MenuItem } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ButtonInc from '../../../shared-ui/ButtonInc';
import ModalInc from '../../../shared-ui/ModalInc';
import AlertDialog from '../../../components/AlertDialog/index.es6';
import SkeletonWrapper from '../../NewProductList/components/SkeletonWrapper/SkeletonWrapper';
import {
  getOrgClaimInitConfigsRequest,
  createOrgClaimInitConfigRequest,
  updateOrgClaimInitConfigRequest,
  deleteOrgClaimInitConfigRequest,
  resetCreateOrgClaimInitConfigError,
  resetUpdateOrgClaimInitConfigError,
  resetDeleteOrgClaimInitConfigError,
} from '../../OrganizationClaimInitiation/Redux/actions';
import { formatDateLocale } from '../../../lib/utils.es6';

var FIELD_TYPES = ['text', 'number', 'date', 'select', 'boolean', 'phone', 'currency'];

var EMPTY_FORM = {
  code: '',
  label: '',
  description: '',
  active: true,
  claim_types: [],
  fields: [],
  documents: [],
};

function emptyClaimType() {
  return { code: '', label: '' };
}

function emptyField() {
  return { name: '', label: '', field_type: 'text', required: false };
}

function emptyDocument() {
  return { code: '', label: '', required: false };
}

var inputStyle = {
  display: 'block',
  width: '100%',
  padding: '6px 8px',
  boxSizing: 'border-box',
  borderRadius: 4,
  border: '1px solid #D1D5DB',
  fontSize: 13,
  lineHeight: '20px',
  outline: 'none',
};

var sectionHeadingStyle = {
  fontWeight: 700,
  fontSize: 13,
  color: '#374151',
  marginBottom: 6,
  marginTop: 4,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
};

var labelStyle = {
  fontWeight: 600,
  fontSize: 13,
  color: '#374151',
};

var rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 6,
  background: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: 4,
  padding: '6px 8px',
};

var dragHandleStyle = {
  cursor: 'grab',
  color: '#9CA3AF',
  fontSize: 16,
  userSelect: 'none',
  flexShrink: 0,
  paddingRight: 4,
};

var removeBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#B91C1C',
  fontSize: 16,
  padding: '0 4px',
  lineHeight: 1,
  flexShrink: 0,
};

var addBtnStyle = {
  background: 'none',
  border: '1px dashed #6B7280',
  borderRadius: 4,
  padding: '5px 12px',
  cursor: 'pointer',
  fontSize: 12,
  color: '#374151',
  marginTop: 4,
};

class ClaimInitiationTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showAddModal: false,
      showEditModal: false,
      showDeleteConfirm: false,
      selectedItem: null,
      form: Object.assign({}, EMPTY_FORM, {
        claim_types: [],
        fields: [],
        documents: [],
      }),
    };
    this.openAdd = this.openAdd.bind(this);
    this.openEdit = this.openEdit.bind(this);
    this.openDelete = this.openDelete.bind(this);
    this.handleCreate = this.handleCreate.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.setField = this.setField.bind(this);
    this.addClaimType = this.addClaimType.bind(this);
    this.removeClaimType = this.removeClaimType.bind(this);
    this.updateClaimType = this.updateClaimType.bind(this);
    this.addField = this.addField.bind(this);
    this.removeField = this.removeField.bind(this);
    this.updateField = this.updateField.bind(this);
    this.onFieldDragEnd = this.onFieldDragEnd.bind(this);
    this.addDocument = this.addDocument.bind(this);
    this.removeDocument = this.removeDocument.bind(this);
    this.updateDocument = this.updateDocument.bind(this);
  }

  componentWillMount() {
    this.props.dispatch(
      getOrgClaimInitConfigsRequest(this.props.organizationGuid)
    );
  }

  componentDidUpdate(prevProps) {
    var d = this.props.orgClaimInitData;
    var p = prevProps.orgClaimInitData;
    if (d.created && !p.created) {
      this.setState({
        showAddModal: false,
        form: Object.assign({}, EMPTY_FORM, { claim_types: [], fields: [], documents: [] }),
      });
    }
    if (d.updated && !p.updated) {
      this.setState({ showEditModal: false, selectedItem: null });
    }
    if (d.deleted && !p.deleted) {
      this.setState({ showDeleteConfirm: false, selectedItem: null });
    }
  }

  openAdd() {
    this.setState({
      showAddModal: true,
      form: Object.assign({}, EMPTY_FORM, { claim_types: [], fields: [], documents: [] }),
    });
  }

  openEdit(item) {
    var cd = item.claim_init_data || {};
    this.setState({
      showEditModal: true,
      selectedItem: item,
      form: {
        code: item.code || '',
        label: cd.label || '',
        description: cd.description || '',
        active: item.active !== false,
        claim_types: cd.claim_types
          ? cd.claim_types.map(function(ct) {
              return { code: ct.code || '', label: ct.label || '' };
            })
          : [],
        fields: cd.fields
          ? cd.fields.map(function(fi) {
              return {
                name: fi.name || '',
                label: fi.label || '',
                field_type: fi.field_type || 'text',
                required: fi.required === true,
              };
            })
          : [],
        documents: cd.documents
          ? cd.documents.map(function(doc) {
              return {
                code: doc.code || '',
                label: doc.label || '',
                required: doc.required === true,
              };
            })
          : [],
      },
    });
  }

  openDelete(item) {
    this.setState({ showDeleteConfirm: true, selectedItem: item });
  }

  setField(name, value) {
    this.setState(function(s) {
      return { form: Object.assign({}, s.form, { [name]: value }) };
    });
  }

  // ── Claim Types ──────────────────────────────────────────────────────────

  addClaimType() {
    this.setState(function(s) {
      var next = s.form.claim_types.concat([emptyClaimType()]);
      return { form: Object.assign({}, s.form, { claim_types: next }) };
    });
  }

  removeClaimType(i) {
    this.setState(function(s) {
      var next = s.form.claim_types.filter(function(_, idx) { return idx !== i; });
      return { form: Object.assign({}, s.form, { claim_types: next }) };
    });
  }

  updateClaimType(i, key, val) {
    this.setState(function(s) {
      var next = s.form.claim_types.map(function(ct, idx) {
        if (idx !== i) { return ct; }
        return Object.assign({}, ct, { [key]: val });
      });
      return { form: Object.assign({}, s.form, { claim_types: next }) };
    });
  }

  // ── Fields ───────────────────────────────────────────────────────────────

  addField() {
    this.setState(function(s) {
      var next = s.form.fields.concat([emptyField()]);
      return { form: Object.assign({}, s.form, { fields: next }) };
    });
  }

  removeField(i) {
    this.setState(function(s) {
      var next = s.form.fields.filter(function(_, idx) { return idx !== i; });
      return { form: Object.assign({}, s.form, { fields: next }) };
    });
  }

  updateField(i, key, val) {
    this.setState(function(s) {
      var next = s.form.fields.map(function(fi, idx) {
        if (idx !== i) { return fi; }
        return Object.assign({}, fi, { [key]: val });
      });
      return { form: Object.assign({}, s.form, { fields: next }) };
    });
  }

  onFieldDragEnd(result) {
    if (!result.destination) { return; }
    var src = result.source.index;
    var dst = result.destination.index;
    if (src === dst) { return; }
    this.setState(function(s) {
      var arr = s.form.fields.slice();
      var moved = arr.splice(src, 1)[0];
      arr.splice(dst, 0, moved);
      return { form: Object.assign({}, s.form, { fields: arr }) };
    });
  }

  // ── Documents ────────────────────────────────────────────────────────────

  addDocument() {
    this.setState(function(s) {
      var next = s.form.documents.concat([emptyDocument()]);
      return { form: Object.assign({}, s.form, { documents: next }) };
    });
  }

  removeDocument(i) {
    this.setState(function(s) {
      var next = s.form.documents.filter(function(_, idx) { return idx !== i; });
      return { form: Object.assign({}, s.form, { documents: next }) };
    });
  }

  updateDocument(i, key, val) {
    this.setState(function(s) {
      var next = s.form.documents.map(function(doc, idx) {
        if (idx !== i) { return doc; }
        return Object.assign({}, doc, { [key]: val });
      });
      return { form: Object.assign({}, s.form, { documents: next }) };
    });
  }

  // ── Payload ──────────────────────────────────────────────────────────────

  buildPayload() {
    var f = this.state.form;
    return {
      organization_guid: this.props.organizationGuid,
      code: f.code,
      active: f.active,
      claim_init_data: {
        label: f.label,
        description: f.description,
        claim_types: f.claim_types,
        fields: f.fields,
        documents: f.documents,
      },
    };
  }

  handleCreate() {
    this.props.dispatch(createOrgClaimInitConfigRequest(this.buildPayload()));
  }

  handleUpdate() {
    var payload = Object.assign({}, this.buildPayload(), {
      guid: this.state.selectedItem.guid,
    });
    this.props.dispatch(updateOrgClaimInitConfigRequest(payload));
  }

  handleDelete() {
    this.props.dispatch(deleteOrgClaimInitConfigRequest({
      guid: this.state.selectedItem.guid,
      organization_guid: this.props.organizationGuid,
    }));
  }

  // ── Render sections ──────────────────────────────────────────────────────

  renderBasicInfo() {
    var f = this.state.form;
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <label style={labelStyle}>Code *</label>
          <input
            style={inputStyle}
            value={f.code}
            onChange={function(e) { this.setField('code', e.target.value); }.bind(this)}
            placeholder="e.g. funeral_claim"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <label style={labelStyle}>Label</label>
          <input
            style={inputStyle}
            value={f.label}
            onChange={function(e) { this.setField('label', e.target.value); }.bind(this)}
            placeholder="Funeral Claim Initiation"
          />
        </Grid>
        <Grid item xs={12}>
          <label style={labelStyle}>Description</label>
          <input
            style={inputStyle}
            value={f.description}
            onChange={function(e) { this.setField('description', e.target.value); }.bind(this)}
          />
        </Grid>
        <Grid item xs={12}>
          <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={f.active}
              onChange={function(e) { this.setField('active', e.target.checked); }.bind(this)}
              style={{ marginRight: 8 }}
            />
            Active
          </label>
        </Grid>
      </Grid>
    );
  }

  renderClaimTypes() {
    var f = this.state.form;
    var self = this;
    return (
      <Box sx={{ mt: 3 }}>
        <Typography style={sectionHeadingStyle}>Claim Types</Typography>
        {f.claim_types.map(function(ct, i) {
          return (
            <div key={i} style={rowStyle}>
              <input
                style={Object.assign({}, inputStyle, { flex: 1 })}
                value={ct.code}
                placeholder="code"
                onChange={function(e) { self.updateClaimType(i, 'code', e.target.value); }}
              />
              <input
                style={Object.assign({}, inputStyle, { flex: 2 })}
                value={ct.label}
                placeholder="Label"
                onChange={function(e) { self.updateClaimType(i, 'label', e.target.value); }}
              />
              <button
                style={removeBtnStyle}
                onClick={function() { self.removeClaimType(i); }}
                title="Remove"
              >
                ×
              </button>
            </div>
          );
        })}
        <button style={addBtnStyle} onClick={this.addClaimType}>
          + Add Claim Type
        </button>
      </Box>
    );
  }

  renderFields() {
    var f = this.state.form;
    var self = this;
    return (
      <Box sx={{ mt: 3 }}>
        <Typography style={sectionHeadingStyle}>Form Fields</Typography>
        <DragDropContext onDragEnd={this.onFieldDragEnd}>
          <Droppable droppableId="fields-list">
            {function(provided) {
              return (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {f.fields.map(function(fi, i) {
                    return (
                      <Draggable key={String(i)} draggableId={'field-' + i} index={i}>
                        {function(dragProvided, snapshot) {
                          return (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              style={Object.assign(
                                {},
                                rowStyle,
                                snapshot.isDragging
                                  ? { background: '#EFF6FF', border: '1px solid #BFDBFE' }
                                  : {},
                                dragProvided.draggableProps.style
                              )}
                            >
                              <span
                                style={dragHandleStyle}
                                {...dragProvided.dragHandleProps}
                                title="Drag to reorder"
                              >
                                ⠿
                              </span>
                              <input
                                style={Object.assign({}, inputStyle, { flex: 2 })}
                                value={fi.name}
                                placeholder="field_name"
                                onChange={function(e) { self.updateField(i, 'name', e.target.value); }}
                              />
                              <input
                                style={Object.assign({}, inputStyle, { flex: 3 })}
                                value={fi.label}
                                placeholder="Field Label"
                                onChange={function(e) { self.updateField(i, 'label', e.target.value); }}
                              />
                              <Select
                                size="small"
                                value={fi.field_type || 'text'}
                                onChange={function(e) { self.updateField(i, 'field_type', e.target.value); }}
                                sx={{
                                  fontSize: 12,
                                  minWidth: 90,
                                  height: 32,
                                  flexShrink: 0,
                                }}
                              >
                                {FIELD_TYPES.map(function(t) {
                                  return (
                                    <MenuItem key={t} value={t} sx={{ fontSize: 12 }}>
                                      {t}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                <Typography sx={{ fontSize: 11, color: '#6B7280', mr: 0.25 }}>
                                  Req
                                </Typography>
                                <Switch
                                  size="small"
                                  checked={fi.required === true}
                                  onChange={function(e) { self.updateField(i, 'required', e.target.checked); }}
                                  sx={{ transform: 'scale(0.8)' }}
                                />
                              </Box>
                              <button
                                style={removeBtnStyle}
                                onClick={function() { self.removeField(i); }}
                                title="Remove"
                              >
                                ×
                              </button>
                            </div>
                          );
                        }}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              );
            }}
          </Droppable>
        </DragDropContext>
        <button style={addBtnStyle} onClick={this.addField}>
          + Add Field
        </button>
      </Box>
    );
  }

  renderDocuments() {
    var f = this.state.form;
    var self = this;
    return (
      <Box sx={{ mt: 3 }}>
        <Typography style={sectionHeadingStyle}>Required Documents</Typography>
        {f.documents.map(function(doc, i) {
          return (
            <div key={i} style={rowStyle}>
              <input
                style={Object.assign({}, inputStyle, { flex: 1 })}
                value={doc.code}
                placeholder="code"
                onChange={function(e) { self.updateDocument(i, 'code', e.target.value); }}
              />
              <input
                style={Object.assign({}, inputStyle, { flex: 2 })}
                value={doc.label}
                placeholder="Label"
                onChange={function(e) { self.updateDocument(i, 'label', e.target.value); }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Typography sx={{ fontSize: 11, color: '#6B7280', mr: 0.25 }}>
                  Req
                </Typography>
                <Switch
                  size="small"
                  checked={doc.required === true}
                  onChange={function(e) { self.updateDocument(i, 'required', e.target.checked); }}
                  sx={{ transform: 'scale(0.8)' }}
                />
              </Box>
              <button
                style={removeBtnStyle}
                onClick={function() { self.removeDocument(i); }}
                title="Remove"
              >
                ×
              </button>
            </div>
          );
        })}
        <button style={addBtnStyle} onClick={this.addDocument}>
          + Add Document
        </button>
      </Box>
    );
  }

  renderFormFields() {
    return (
      <Box sx={{ pt: 1 }}>
        {this.renderBasicInfo()}
        {this.renderClaimTypes()}
        {this.renderFields()}
        {this.renderDocuments()}
      </Box>
    );
  }

  renderCard(item) {
    var cd = item.claim_init_data || {};
    var claimTypes = cd.claim_types || [];
    return (
      <Grid item xs={12} sm={6} md={4} key={item.guid}>
        <Box sx={{
          border: '1px solid #E5E7EB',
          borderRadius: 2,
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{
            px: 2.5, pt: 2.5, pb: 1.5,
            borderBottom: '1px solid #F3F4F6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}>
            <Box>
              <Typography variant="subtitle2"
                sx={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
                {cd.label || item.code}
              </Typography>
              <Typography variant="body2"
                sx={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace', mt: 0.25 }}>
                {item.code}
              </Typography>
            </Box>
            <Chip
              label={item.active !== false ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                height: 20, fontSize: 11, fontWeight: 600,
                bgcolor: item.active !== false ? '#F0FDF4' : '#FEF2F2',
                color: item.active !== false ? '#15803D' : '#B91C1C',
                border: item.active !== false ? '1px solid #BBF7D0' : '1px solid #FECACA',
              }}
            />
          </Box>
          <Box sx={{ px: 2.5, py: 1.5, flex: 1 }}>
            {cd.description && (
              <Typography variant="body2"
                sx={{ fontSize: 12, color: '#6B7280', mb: 1.5 }}>
                {cd.description}
              </Typography>
            )}
            {claimTypes.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                {claimTypes.map(function(ct) {
                  return (
                    <Chip
                      key={ct.code}
                      label={ct.label || ct.code}
                      size="small"
                      sx={{
                        height: 18, fontSize: 10,
                        bgcolor: '#EFF6FF', color: '#1D4ED8',
                        border: '1px solid #BFDBFE',
                      }}
                    />
                  );
                })}
              </Box>
            )}
            <Typography variant="body2" sx={{ fontSize: 11, color: '#9CA3AF' }}>
              Fields: {(cd.fields && cd.fields.length) || 0} &nbsp;·&nbsp;
              Docs: {(cd.documents && cd.documents.length) || 0}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 11, color: '#9CA3AF', mt: 0.5 }}>
              Created: {item.created_at ? formatDateLocale(item.created_at) : '—'}
            </Typography>
          </Box>
          <Box sx={{
            px: 2.5, py: 1.5,
            borderTop: '1px solid #F3F4F6',
            display: 'flex', gap: 1,
          }}>
            <ButtonInc
              btnType="secondary"
              onClick={function() { this.openEdit(item); }.bind(this)}
              style={{ flex: 1, fontSize: 12 }}
            >
              Edit
            </ButtonInc>
            <ButtonInc
              btnType="secondary"
              onClick={function() { this.openDelete(item); }.bind(this)}
              style={{ flex: 1, fontSize: 12, color: '#B91C1C', borderColor: '#FECACA' }}
            >
              Delete
            </ButtonInc>
          </Box>
        </Box>
      </Grid>
    );
  }

  render() {
    var d = this.props.orgClaimInitData;
    var s = this.state;
    var list = d.list || [];

    if (d.loading) { return <SkeletonWrapper loading={true} />; }

    return (
      <div>
        <Box sx={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6"
              sx={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>
              Claim Initiation Configs
            </Typography>
            <Typography variant="body2"
              sx={{ fontSize: 13, color: '#6B7280', mt: 0.25 }}>
              Define claim types, fields, and documents per journey.
            </Typography>
          </Box>
          <ButtonInc variant="contained" onClick={this.openAdd}>
            + Add Config
          </ButtonInc>
        </Box>

        {list.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: '#9CA3AF' }}>
            <Typography variant="body1" sx={{ fontSize: 15 }}>
              No claim initiation configs yet.
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 13, mt: 0.5 }}>
              Add one to start configuring claim journeys for this organization.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {list.map(this.renderCard.bind(this))}
          </Grid>
        )}

        <ModalInc
          modalTitle="Add Claim Initiation Config"
          open={s.showAddModal}
          close={function() { this.setState({ showAddModal: false }); }.bind(this)}
          fullWidth
          maxWidth="lg"
          modalButtons={[
            <ButtonInc key="cancel" btnType="secondary"
              onClick={function() { this.setState({ showAddModal: false }); }.bind(this)}>
              Cancel
            </ButtonInc>,
            <ButtonInc key="save" variant="contained" loading={d.creating}
              onClick={this.handleCreate}>
              Save
            </ButtonInc>,
          ]}
        >
          {this.renderFormFields()}
        </ModalInc>

        <ModalInc
          modalTitle="Edit Claim Initiation Config"
          open={s.showEditModal}
          close={function() { this.setState({ showEditModal: false }); }.bind(this)}
          fullWidth
          maxWidth="lg"
          modalButtons={[
            <ButtonInc key="cancel" btnType="secondary"
              onClick={function() { this.setState({ showEditModal: false }); }.bind(this)}>
              Cancel
            </ButtonInc>,
            <ButtonInc key="save" variant="contained" loading={d.updating}
              onClick={this.handleUpdate}>
              Save
            </ButtonInc>,
          ]}
        >
          {this.renderFormFields()}
        </ModalInc>

        {s.showDeleteConfirm && (
          <AlertDialog
            show={s.showDeleteConfirm}
            title="Delete Config"
            type="warning"
            showConfirm
            confirmBtnText="Delete"
            showCancel
            onConfirm={this.handleDelete}
            onCancel={function() {
              this.setState({ showDeleteConfirm: false, selectedItem: null });
            }.bind(this)}
          >
            Delete <strong>{s.selectedItem && s.selectedItem.code}</strong>?
          </AlertDialog>
        )}

        {d.createError && (
          <AlertDialog
            show={!!d.createError}
            title="Error"
            type="danger"
            showConfirm
            confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetCreateOrgClaimInitConfigError());
            }.bind(this)}
          >
            {d.createError.message || JSON.stringify(d.createError)}
          </AlertDialog>
        )}
        {d.updateError && (
          <AlertDialog
            show={!!d.updateError}
            title="Error"
            type="danger"
            showConfirm
            confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetUpdateOrgClaimInitConfigError());
            }.bind(this)}
          >
            {d.updateError.message || JSON.stringify(d.updateError)}
          </AlertDialog>
        )}
        {d.deleteError && (
          <AlertDialog
            show={!!d.deleteError}
            title="Error"
            type="danger"
            showConfirm
            confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetDeleteOrgClaimInitConfigError());
            }.bind(this)}
          >
            {d.deleteError.message || JSON.stringify(d.deleteError)}
          </AlertDialog>
        )}
      </div>
    );
  }
}

export default connect(function(state) {
  return { orgClaimInitData: state.orgClaimInitConfigsData };
})(ClaimInitiationTab);
